import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCappedCartQuantity, isProductTracked } from 'utils/stockPolicy';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error loading cart:', error);
        localStorage.removeItem('cart');
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, quantity = 1) => {
    setCart(prev => {
      // Create a unique identifier considering size if present
      const existing = prev.find(item => 
        item.id === product.id && 
        (product.selectedSize ? item.selectedSize === product.selectedSize : !item.selectedSize)
      );

      if (existing) {
        return prev.map(item =>
          item.id === product.id && 
          (product.selectedSize ? item.selectedSize === product.selectedSize : !item.selectedSize)
            ? { 
                ...item, 
                cartQuantity: getCappedCartQuantity(
                  (item.cartQuantity ?? item.quantity ?? 0) + quantity,
                  product
                ),
              }
            : item
        );
      }

      const initialCartQuantity = getCappedCartQuantity(quantity, product);
      if (initialCartQuantity <= 0) {
        return prev;
      }

      const trackedStockQuantity = isProductTracked(product)
        ? (product.quantity ?? product.qty ?? 0)
        : null;

      // Store stock quantity separately from cart quantity
      return [...prev, { 
        ...product, 
        stockQuantity: trackedStockQuantity, // Original stock for tracked items only
        cartQuantity: initialCartQuantity // Cart quantity
      }];
    });
  };

  const removeFromCart = (id, selectedSize = null) => {
    setCart(prev => prev.filter(item => {
      if (selectedSize) {
        return !(item.id === id && item.selectedSize === selectedSize);
      }
      return item.id !== id;
    }));
  };

  const updateQuantity = (id, newQuantity, selectedSize = null) => {
    if (newQuantity <= 0) return removeFromCart(id, selectedSize);

    setCart(prev =>
      prev.map(item => {
        const isMatch = selectedSize 
          ? (item.id === id && item.selectedSize === selectedSize)
          : item.id === id;

        if (isMatch) {
          const nextCartQuantity = getCappedCartQuantity(newQuantity, item);
          return { 
            ...item, 
            cartQuantity: nextCartQuantity
          };
        }
        
        return item;
      })
    );
  };

  const clearCart = () => setCart([]);

  const getUnitPrice = (item) => {
    const rawPrice = item?.selling_price ?? item?.price ?? 0;
    const parsedPrice = Number(rawPrice);
    return Number.isFinite(parsedPrice) ? parsedPrice : 0;
  };

  const getCartItemQuantity = (item) => {
    const rawQuantity = item?.cartQuantity ?? item?.quantity ?? 0;
    const parsedQuantity = Number(rawQuantity);
    return Number.isFinite(parsedQuantity) ? parsedQuantity : 0;
  };

  const getCartTotal = () =>
    cart.reduce((total, item) => {
      const quantity = getCartItemQuantity(item);
      const unitPrice = getUnitPrice(item);
      return total + unitPrice * quantity;
    }, 0);

  const getCartCount = () =>
    cart.reduce((count, item) => {
      const quantity = getCartItemQuantity(item);
      return count + quantity;
    }, 0);

  const isInCart = (id, selectedSize = null) => {
    return cart.some(item => {
      if (selectedSize) {
        return item.id === id && item.selectedSize === selectedSize;
      }
      return item.id === id;
    });
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
        isInCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
